import React, { useState, useEffect } from "react";
import GenericInput from "@ui-components/Input/GenericInput";
import GenericButton from "@ui-components/Button/GenericButton";
import { GenericTag } from "@ui-components/Tag/GenericTag";
import { SkeletonBlock, SkeletonLine } from "@ui-components/Skeleton/GenericSkeleton";
import { useAuth0 } from "@auth0/auth0-react";
import { useAppDispatch, useAppSelector } from "@redux/hooks";
import { setUserProfile } from "@redux/slices/userProfileSlice";
import { getMyProfile, updateMyProfile } from "./request/profileApi";

interface ProfileData {
    firstName: string;
    lastName: string;
    NumberPhone: string;
    position: string;
    email: string;
    Roles: { id: number; name: string }[];
}

const ProfileTab: React.FC = () => {
    const { getAccessTokenSilently } = useAuth0();
    const dispatch = useAppDispatch();
    const userProfile = useAppSelector((state) => state.userProfile);

    const [data, setData] = useState<ProfileData>({
        firstName: "",
        lastName: "",
        NumberPhone: "",
        position: "",
        email: "",
        Roles: [],
    });
    const [originalData, setOriginalData] = useState<ProfileData | null>(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [feedback, setFeedback] = useState<{
        type: "success" | "error";
        message: string;
    } | null>(null);

    useEffect(() => {
        loadProfile();
    // eslint-disable-next-line
    }, []);

    const loadProfile = async () => {
        try {
            setLoading(true);
            const token = await getAccessTokenSilently();
            const response = await getMyProfile(token);
            const profile = response.data;
            const profileData: ProfileData = {
                firstName: profile.firstName || "",
                lastName: profile.lastName || "",
                NumberPhone: profile.NumberPhone || "",
                position: profile.position || "",
                email: profile.email || "",
                Roles: profile.Roles || [],
            };
            setData(profileData);
            setOriginalData(profileData);
        } catch {
            setFeedback({ type: "error", message: "Error loading profile." });
        } finally {
            setLoading(false);
        }
    };

    const hasChanges = (): boolean => {
        if (!originalData) return false;
        return (
            data.firstName !== originalData.firstName ||
            data.lastName !== originalData.lastName ||
            data.NumberPhone !== originalData.NumberPhone ||
            data.position !== originalData.position
        );
    };

    const isValid = (): boolean => {
        return data.firstName.trim().length > 0;
    };

    const handleSave = async () => {
        try {
            setSaving(true);
            setFeedback(null);
            const token = await getAccessTokenSilently();
            const response = await updateMyProfile(token, {
                firstName: data.firstName,
                lastName: data.lastName,
                NumberPhone: data.NumberPhone,
                position: data.position,
            });

            setOriginalData({ ...data });
            setFeedback({ type: "success", message: "Profile updated successfully." });

            // Update Redux store so SideNav reflects the new name
            dispatch(
                setUserProfile({
                    ...userProfile,
                    firstName: response.data.firstName || data.firstName,
                    lastName: response.data.lastName || data.lastName,
                })
            );
        } catch {
            setFeedback({ type: "error", message: "Error updating profile." });
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="space-y-6" aria-busy aria-label="Loading profile">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {[0, 1].map((i) => (
                        <div key={i} className="space-y-1">
                            <SkeletonLine w="30%" h={12} />
                            <SkeletonBlock h={42} roundedClass="rounded-md" />
                        </div>
                    ))}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {[0, 1].map((i) => (
                        <div key={i} className="space-y-1">
                            <SkeletonLine w="30%" h={12} />
                            <SkeletonBlock h={42} roundedClass="rounded-md" />
                        </div>
                    ))}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {[0, 1].map((i) => (
                        <div key={i} className="space-y-1">
                            <SkeletonLine w="30%" h={12} />
                            <SkeletonBlock h={42} roundedClass="rounded-md" />
                        </div>
                    ))}
                </div>
                <div className="flex justify-end pt-4 border-t border-semantic-border-secondary">
                    <SkeletonBlock h={36} w={120} roundedClass="rounded-md" />
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6 mt-8 mb-8">
            {feedback && (
                <div
                    className={`px-4 py-3 rounded-md text-sm ${
                        feedback.type === "success"
                            ? "bg-green-50 text-green-700 border border-green-200"
                            : "bg-red-50 text-red-700 border border-red-200"
                    }`}
                >
                    {feedback.message}
                </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <GenericInput
                    label="First Name"
                    value={data.firstName}
                    onChange={(val) => setData({ ...data, firstName: val })}
                    required
                />
                <GenericInput
                    label="Last Name"
                    value={data.lastName}
                    onChange={(val) => setData({ ...data, lastName: val })}
                />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <GenericInput
                    label="Email"
                    value={data.email}
                    disabled
                    readOnly
                />
                <GenericInput
                    label="Phone Number"
                    value={data.NumberPhone}
                    onChange={(val) => setData({ ...data, NumberPhone: val })}
                />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <GenericInput
                    label="Position"
                    value={data.position}
                    onChange={(val) => setData({ ...data, position: val })}
                />
                <div>
                    <label className="block text-sm font-medium text-semantic-text-primary mb-1">
                        Role
                    </label>
                    <div className="flex items-center h-[42px]">
                        {data.Roles.map((role) => (
                            <GenericTag
                                key={role.id}
                                label={role.name}
                                variant="default"
                                size="medium"
                            />
                        ))}
                    </div>
                </div>
            </div>

            <div className="flex justify-end pt-4 border-t border-semantic-border-secondary">
                <GenericButton
                    label={saving ? "Saving..." : "Save Changes"}
                    variant="brand"
                    onClick={handleSave}
                    disabled={!hasChanges() || !isValid() || saving}
                />
            </div>
        </div>
    );
};

export default ProfileTab;
